import { api } from "./client";

export type CatalogInfo = {
  kind: string;
  name: string;
  schemaMigration?: string | null;
  sourceRevision?: string | null;
  lookupContractVersion?: "material_lookup.v1";
  lookupDefinitionId?: string;
  lookupKind?: "material" | "section" | "profile" | "product" | "installation_condition" | "assembly";
  calculatorFamily?: "wood" | "steel" | "cold_formed_steel" | "concrete" | "masonry" | "connector";
};

export type CatalogFacetOption = {
  value: string;
  count: number;
};

export type CatalogPage<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  facets: Record<string, CatalogFacetOption[]>;
  catalog: CatalogInfo;
  /** Rows hidden by the opt-in capacity/demand filter (absent when off). */
  hiddenCount?: number;
  /** ASD-only rows not shown because the sheet method is LRFD. */
  methodHiddenCount?: number;
  /** Fasteners search only: the engine's standard NDS nail sizes, pinned to
      the first page as a distinct group — never part of items/total/facets. */
  standardNails?: T[];
};

export type ConnectorCatalogItem = {
  catalogKey: string;
  manufacturer: string;
  family: string;
  roleFamily?: string | null;
  role?: string | null;
  subfamily?: string | null;
  category?: string | null;
  series?: string | null;
  model?: string | null;
  designation?: string | null;
  name: string;
  capacityPublished: boolean;
  occurrenceCount: number;
  installationCount: number;
  referenceMaxDownloadLb?: number | null;
  referenceMaxUpliftLb?: number | null;
  applicability: "reference_match_only";
};

export type FastenerCatalogItem = {
  catalogKey: string;
  /** "nds_standard" marks engine-data standard nails (Appendix L / Table I1);
      absent on database (proprietary / evaluated) fasteners. */
  source?: string;
  manufacturer: string;
  designation: string;
  series?: string | null;
  name: string;
  /** Standard NDS nails only. */
  nailType?: string;
  pennyweight?: string;
  lengthIn?: number | null;
  threadLengthIn?: number | null;
  diameterIn?: number | null;
  fybPsi?: number | null;
  asdTensionLb?: number | null;
  asdShearLb?: number | null;
  lrfdTensionLb?: number | null;
  lrfdShearLb?: number | null;
  material?: string | null;
  finish?: string | null;
  sourceDocument?: string | null;
  sourceUrl?: string | null;
  compatibleCalculators: string[];
};

export type ConnectorCapacityCatalogItem = {
  capacityKey: string;
  capacitySetId: string;
  sourceKind: "installation" | "load_condition" | "capacity_condition" | "wood_capacity";
  installationKey: string;
  manufacturer: string;
  designation: string;
  name: string;
  family?: string | null;
  installationLabel?: string | null;
  woodSpeciesGroup?: string | null;
  woodSpecificGravity?: number | null;
  fastenerSchedule?: string | null;
  direction: string;
  designMethod: "ASD" | "LRFD";
  loadDurationCd?: number | null;
  capacityLb: number;
  conditionSummary?: string | null;
  loadDurationBasis?: string | null;
  sourceDocument?: string | null;
  sourceTable?: string | null;
  sourcePage?: string | null;
  sourceEdition?: string | null;
  applicability: "calculation_ready";
};

export type WallProductCatalogItem = {
  catalogKey: string;
  category: string;
  subcategory?: string | null;
  selectionRole: string;
  assemblyRole: string;
  manufacturer: string;
  brand?: string | null;
  model?: string | null;
  designation?: string | null;
  name: string;
  weightPsf: number;
  completenessRank?: number | null;
};

export type CalculatorCatalogFact = {
  label: string;
  value: string | number | boolean;
  unit?: string | null;
};

export type CalculatorCatalogItem = {
  stableKey: string;
  primary: string;
  secondary?: string | null;
  facts: CalculatorCatalogFact[];
  sourceUrl?: string | null;
  /** Design-value basis shared by the row's capacity facts (e.g. "ASD"). */
  basis?: string | null;
  /** Present only when the sheet supplied demand context. */
  meetsDemand?: boolean;
};

export type CalculatorBindingContext = {
  demands?: Partial<Record<"download" | "uplift" | "tension", number>>;
  designMethod?: string;
};

function withQuery(path: string, params: Record<string, string | number | boolean | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") query.set(key, String(value));
  }
  const suffix = query.toString();
  return suffix ? `${path}?${suffix}` : path;
}

export function searchConnectors(params: {
  search?: string;
  family?: string;
  manufacturer?: string;
  capacityOnly?: boolean;
  page?: number;
  pageSize?: number;
} = {}) {
  return api.get<CatalogPage<ConnectorCatalogItem>>(
    withQuery("/api/materials/connectors/search", params),
  );
}

export function searchFasteners(params: {
  search?: string;
  manufacturer?: string;
  page?: number;
  pageSize?: number;
} = {}) {
  return api.get<CatalogPage<FastenerCatalogItem>>(
    withQuery("/api/materials/connectors/fasteners/search", params),
  );
}

export function searchConnectorCapacities(params: {
  search?: string;
  family?: string;
  manufacturer?: string;
  direction?: string;
  designMethod: "ASD" | "LRFD";
  minCapacityLb?: number;
  page?: number;
  pageSize?: number;
}) {
  return api.get<CatalogPage<ConnectorCapacityCatalogItem>>(
    withQuery("/api/materials/connectors/capacities/search", params),
  );
}

export function searchWallProducts(params: {
  search?: string;
  category?: string;
  manufacturer?: string;
  page?: number;
  pageSize?: number;
} = {}) {
  return api.get<CatalogPage<WallProductCatalogItem>>(
    withQuery("/api/materials/ceiling-wall/search", params),
  );
}

export type CatalogDisplayResolution = {
  stableKey: string;
  primary: string;
  secondary?: string | null;
  catalogId?: string | null;
  datasetRevision?: string | null;
};

/** Resolve one persisted stable key to its display name (read-only rename
    service for sheets saved before the picker wrote label sidecars). */
export function resolveCalculatorBindingDisplay(
  calculatorId: string,
  bindingId: string,
  stableKey: string,
) {
  return api.get<CatalogDisplayResolution>(
    withQuery(
      `/api/materials/calculators/${encodeURIComponent(calculatorId)}/bindings/${encodeURIComponent(bindingId)}/resolve-display`,
      { stableKey },
    ),
  );
}

export function searchCalculatorBinding(
  calculatorId: string,
  bindingId: string,
  params: {
    search?: string;
    dependencyKeys?: Record<string, string>;
    facetFilters?: Record<string, string[]>;
    context?: CalculatorBindingContext;
    capacityFilter?: boolean;
    page?: number;
    pageSize?: number;
  } = {},
) {
  const hasFacetFilters = Object.values(params.facetFilters ?? {}).some((values) => values.length > 0);
  const hasContext = Object.values(params.context?.demands ?? {}).some((value) => (value ?? 0) > 0);
  return api.get<CatalogPage<CalculatorCatalogItem>>(
    withQuery(
      `/api/materials/calculators/${encodeURIComponent(calculatorId)}/bindings/${encodeURIComponent(bindingId)}/search`,
      {
        search: params.search,
        dependencyKeys: JSON.stringify(params.dependencyKeys ?? {}),
        // Only entities that support narrowing accept these; omitting them
        // keeps every other binding on the untouched generic contract.
        facetFilters: hasFacetFilters ? JSON.stringify(params.facetFilters) : undefined,
        context: hasContext ? JSON.stringify(params.context) : undefined,
        capacityFilter: params.capacityFilter ? true : undefined,
        page: params.page,
        pageSize: params.pageSize,
      },
    ),
  );
}
